import type { GameSettings } from '../types';

export type SoundCue = 'byte' | 'power-up' | 'router' | 'firewall' | 'internet' | 'server' | 'countdown' | 'warning' | 'win' | 'disconnect';

const CUES: Record<SoundCue, number[]> = {
  byte: [660, 880], 'power-up': [440, 660, 990], router: [330, 440, 660], firewall: [220, 330, 550],
  internet: [392, 587, 784], server: [294, 440, 587, 880], countdown: [380], warning: [180, 180],
  win: [392, 523, 659, 784], disconnect: [300, 190, 120],
};

export class AudioSystem {
  private context?: AudioContext;
  private unlocked = false;
  private musicTimer?: number;
  private musicStep = 0;

  constructor(private readonly getSettings: () => GameSettings) {}

  async unlock(): Promise<void> {
    if (!this.context) this.context = new AudioContext();
    if (this.context.state === 'suspended') await this.context.resume();
    this.unlocked = true;
    this.startMusic();
  }

  play(cue: SoundCue): void {
    const settings = this.getSettings();
    if (!this.unlocked || !this.context || !settings.audioEnabled || settings.masterVolume <= 0) return;
    const now = this.context.currentTime;
    CUES[cue].forEach((frequency, index) => this.tone(frequency, now + index * 0.065, cue === 'disconnect' ? 'sawtooth' : 'sine', settings.masterVolume * 0.115, 0.14));
  }

  private startMusic(): void {
    if (this.musicTimer) return;
    const notes = [98, 147, 196, 147, 110, 165, 220, 165];
    this.musicTimer = window.setInterval(() => {
      const settings = this.getSettings();
      if (!this.context || !settings.audioEnabled || settings.masterVolume <= 0) return;
      const note = notes[this.musicStep % notes.length] ?? 98;
      this.musicStep += 1;
      this.tone(note, this.context.currentTime, 'triangle', settings.masterVolume * 0.025, 0.32);
      if (this.musicStep % 4 === 1) this.tone(note * 2, this.context.currentTime + 0.08, 'sine', settings.masterVolume * 0.012, 0.18);
    }, 390);
  }

  private tone(frequency: number, start: number, type: OscillatorType, volume: number, duration: number): void {
    if (!this.context) return;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, start);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume), start + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(gain).connect(this.context.destination);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.01);
  }
}
